import { RefAttributes } from 'react';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import {
  LocalizationProvider,
  LocalizationProviderProps,
} from '@mui/x-date-pickers/LocalizationProvider';
import { Locale } from 'date-fns/locale';
import { enAU } from 'date-fns/locale/en-AU';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
} from 'react-hook-form';
import { CustomPickerTextField } from '../custom/CustomPickerTextField';
import { CalendarIcon } from '@/assets/icons';
import { PickerValidDate } from '@mui/x-date-pickers/models';

type DatePickerProps<
  TEnableAccessibleFieldDOMStructure extends boolean = false,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = MuiDatePickerProps<TEnableAccessibleFieldDOMStructure> &
  RefAttributes<HTMLDivElement> & {
    ControllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
    LocalizationProviderProps?: LocalizationProviderProps<Locale>;
  };

export const DatePicker = <
  TDate extends PickerValidDate,
  TEnableAccessibleFieldDOMStructure extends boolean = false,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ControllerProps,
  LocalizationProviderProps,
  ...props
}: DatePickerProps<
  TEnableAccessibleFieldDOMStructure,
  TFieldValues,
  TName
>) => {
  return (
    <Controller
      {...ControllerProps}
      render={({ field: { ref, value, ...field }, fieldState: { error } }) => {
        return (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={enAU}
            {...LocalizationProviderProps}
          >
            <MuiDatePicker
              {...props}
              {...field}
              inputRef={ref}
              value={value as TDate | null}
              slots={{
                openPickerIcon: CalendarIcon,
                textField: CustomPickerTextField,
              }}
              slotProps={{
                ...props.slotProps,
                textField: {
                  ...field,
                  helperText: error?.message,
                  ...props.slotProps?.textField,
                  error: Boolean(error),
                },
              }}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
};
